import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PlayCircle, CheckCircle2, AlertCircle, Clock, RefreshCw } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const AutomationStatus = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);

  const handleRunAutomation = async () => {
    setIsRunning(true);
    setLastResult(null);
    
    toast.info("Starting automation pipeline...", {
      description: "This may take 2-3 minutes to complete all layers"
    });

    try {
      const { data, error } = await supabase.functions.invoke('daily-automation', {
        body: { force: true }
      });
      
      if (error) {
        throw {
          message: error.message,
          details: error.details || error.context || 'No additional details',
          data: data
        };
      }

      // Check if skipped
      if (data?.skipped) {
        setLastResult({
          success: false,
          message: data.reason,
          details: `Last run: ${new Date(data.lastRun).toLocaleString()}`
        });
        toast.warning("Automation Skipped", {
          description: data.reason
        });
        return;
      }

      // Check for step failures
      if (data?.steps) {
        const failedSteps = data.steps.filter((s: any) => s.status === 'failed');
        if (failedSteps.length > 0) {
          setLastResult({
            success: false,
            message: 'Automation completed with errors',
            failedSteps,
            data
          });
          toast.error("Automation had errors", {
            description: `${failedSteps.length} step(s) failed - see details below`
          });
          return;
        }
      }

      // Success
      const successCount = data.results?.steps?.filter((s: any) => s.status === 'success').length || data.steps?.length || 0;
      setLastResult({
        success: true,
        message: `${successCount} steps completed successfully`,
        data
      });
      toast.success("Automation Complete!", {
        description: `${successCount} steps completed successfully`
      });
    } catch (error: any) {
      console.error("Automation error:", error);
      setLastResult({
        success: false,
        message: error.message || 'Unknown error',
        details: error.details,
        failedSteps: error.failedSteps,
        data: error.data
      });
      toast.error("Automation Failed", {
        description: error.message || "An error occurred during automation"
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card className="p-6 gradient-card border-border/50">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-foreground mb-1">Daily Automation Status</h3>
            <p className="text-sm text-muted-foreground">
              Next scheduled run: Tomorrow at 6:00 AM AEST
            </p>
          </div>
          <Badge variant="outline" className="border-success/30">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Active
          </Badge>
        </div>

        <div className="grid grid-cols-5 gap-2 py-4">
          <div className="text-center">
            <div className="w-10 h-10 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
            </div>
            <p className="text-xs text-muted-foreground">Data</p>
            <p className="text-xs text-muted-foreground">Collection</p>
          </div>
          
          <div className="text-center">
            <div className="w-10 h-10 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
            </div>
            <p className="text-xs text-muted-foreground">AI</p>
            <p className="text-xs text-muted-foreground">Analysis</p>
          </div>
          
          <div className="text-center">
            <div className="w-10 h-10 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
            </div>
            <p className="text-xs text-muted-foreground">Content</p>
            <p className="text-xs text-muted-foreground">Generation</p>
          </div>
          
          <div className="text-center">
            <div className="w-10 h-10 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-5 h-5 text-success" />
            </div>
            <p className="text-xs text-muted-foreground">Auto</p>
            <p className="text-xs text-muted-foreground">Publish</p>
          </div>
          
          <div className="text-center">
            <div className="w-10 h-10 rounded-full bg-warning/10 border border-warning/30 flex items-center justify-center mx-auto mb-2">
              <AlertCircle className="w-5 h-5 text-warning" />
            </div>
            <p className="text-xs text-muted-foreground">Social</p>
            <p className="text-xs text-muted-foreground">Share*</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border/30">
          <p className="text-xs text-muted-foreground">
            * Social sharing requires API configuration
          </p>
          <Button 
            onClick={handleRunAutomation}
            disabled={isRunning}
            size="sm"
            className="bg-gradient-primary"
          >
            {isRunning ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4 mr-2" />
                Run Now
              </>
            )}
          </Button>
        </div>

        {lastResult && (
          <Alert className={`mt-4 ${
            lastResult.success 
              ? 'border-green-600 bg-green-50 dark:bg-green-900/20' 
              : 'border-red-600 bg-red-50 dark:bg-red-900/20'
          }`}>
            <AlertDescription>
              <div className="font-semibold mb-2">
                {lastResult.success ? '✅ Success!' : '❌ Failed'}
              </div>
              <div className="text-sm space-y-2">
                <div className={lastResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                  {lastResult.message}
                </div>
                
                {lastResult.details && (
                  <div className="text-xs opacity-80">
                    {lastResult.details}
                  </div>
                )}

                {lastResult.failedSteps && lastResult.failedSteps.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="font-semibold text-xs">Failed Steps:</div>
                    {lastResult.failedSteps.map((step: any, idx: number) => (
                      <div key={idx} className="pl-2 border-l-2 border-red-400 dark:border-red-600 text-xs">
                        <div className="font-medium">Layer {step.layer}: {step.name}</div>
                        <div className="opacity-80 mt-0.5">{step.error}</div>
                      </div>
                    ))}
                  </div>
                )}

                {lastResult.data && !lastResult.success && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium opacity-70 hover:opacity-100">
                      View Full Response
                    </summary>
                    <pre className="mt-1 p-2 bg-black/10 dark:bg-white/10 rounded text-xs overflow-auto max-h-32">
                      {JSON.stringify(lastResult.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </Card>
  );
};
